import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/api/_admin-guard";

const PARTNERSHIP_TIERS = ["founding", "supporter", "mentor", "exploring"] as const;
const PARTNER_STATUSES = ["new", "contacted", "in_discussion", "committed", "declined"] as const;
const LEARNER_STATUSES = ["new", "invited", "contacted", "enrolled", "declined", "archived"] as const;

const PartnerInterestSchema = z.object({
  organisation: z.string().trim().min(2).max(200),
  contact_name: z.string().trim().min(2).max(160),
  role: z.string().trim().max(120).optional().nullable(),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().nullable(),
  partner_type: z.enum([
    "law_firm",
    "university",
    "corporate",
    "mentor",
    "recruiter",
    "other",
  ]),
  partnership_tier: z.enum(PARTNERSHIP_TIERS).optional().nullable(),
  team_size: z.string().trim().max(40).optional().nullable(),
  website: z
    .string()
    .trim()
    .max(300)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null))
    .refine(
      (v) => !v || /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}([\/?#].*)?$/i.test(v),
      "Enter a valid website URL",
    ),
  country: z.string().trim().max(80).optional().nullable(),
  city: z.string().trim().max(80).optional().nullable(),
  annual_hiring_target: z.string().trim().max(60).optional().nullable(),
  interests: z.array(z.string().trim().max(60)).max(20).default([]),
  message: z.string().trim().max(4000).optional().nullable(),
  attachments: z
    .array(
      z.object({
        path: z.string().min(3).max(400),
        name: z.string().max(200),
        size: z.number().int().min(0).max(20 * 1024 * 1024),
        contentType: z.string().max(120).optional().nullable(),
      }),
    )
    .max(5)
    .default([]),
});

const ATTACH_BUCKET = "partner-onboarding";
const ATTACH_ALLOWED = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "image/webp",
]);
const ATTACH_MAX_BYTES = 20 * 1024 * 1024;

function clientKey(): string {
  const xff = getRequestHeader("x-forwarded-for") ?? "";
  const cf = getRequestHeader("cf-connecting-ip") ?? "";
  const real = getRequestHeader("x-real-ip") ?? "";
  const ip = (cf || xff.split(",")[0] || real || "").trim();
  return ip || "unknown";
}

// Public: partner upload URL — issues a short-lived signed upload URL so the
// anonymous partner form can PUT a file straight to private storage without
// needing an anon INSERT policy on storage.objects.
export const requestPartnerUploadUrl = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        filename: z.string().trim().min(1).max(200),
        size: z.number().int().min(1).max(ATTACH_MAX_BYTES),
        contentType: z.string().max(120),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const { assertRateLimit } = await import("./_rate-limit.server");
      await assertRateLimit({
        scope: "intake:partner_upload",
        key: clientKey(),
        windowMinutes: 15,
        limit: 20,
      });
    } catch (e) {
      throw e instanceof Error ? e : new Error("Rate limit exceeded");
    }
    if (!ATTACH_ALLOWED.has(data.contentType)) {
      throw new Error("File type not allowed. Use PDF, Word, Excel or an image.");
    }
    const safeName = data.filename.replace(/[^\w.\-]+/g, "_").slice(0, 120);
    const path = `pending/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}-${safeName}`;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await (supabaseAdmin as any).storage
      .from(ATTACH_BUCKET)
      .createSignedUploadUrl(path);
    if (error || !signed) throw new Error(error?.message ?? "Could not create upload URL");
    return {
      path,
      token: signed.token as string,
      signedUrl: signed.signedUrl as string,
      maxBytes: ATTACH_MAX_BYTES,
    };
  });

export const submitFoundingPartnerInterest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => PartnerInterestSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const { assertRateLimit } = await import("./_rate-limit.server");
      await assertRateLimit({
        scope: "intake:founding_partner",
        key: clientKey(),
        windowMinutes: 15,
        limit: 5,
      });
    } catch (e) {
      throw e instanceof Error ? e : new Error("Rate limit exceeded");
    }

    // Validate that any claimed attachments actually exist in the bucket
    // and were uploaded via our issued signed URL (prefix `pending/`).
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const validated: typeof data.attachments = [];
    for (const a of data.attachments) {
      if (!a.path.startsWith("pending/")) continue;
      const { data: head } = await (supabaseAdmin as any).storage
        .from(ATTACH_BUCKET)
        .list(a.path.split("/").slice(0, -1).join("/"), {
          search: a.path.split("/").pop(),
          limit: 1,
        });
      if (Array.isArray(head) && head.length > 0) validated.push(a);
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("founding_partner_interest")
      .insert({
        organisation: data.organisation,
        contact_name: data.contact_name,
        role: data.role ?? null,
        email: data.email,
        phone: data.phone ?? null,
        partner_type: data.partner_type,
        partnership_tier: data.partnership_tier ?? null,
        team_size: data.team_size ?? null,
        website: data.website ?? null,
        country: data.country ?? null,
        city: data.city ?? null,
        annual_hiring_target: data.annual_hiring_target ?? null,
        interests: data.interests,
        message: data.message ?? null,
        attachments: validated as any,
      })
      .select("id, created_at")
      .single();
    if (error) {
      console.error("[submitFoundingPartnerInterest]", error);
      throw new Error("Could not submit interest. Please try again.");
    }

    // Confirmation email (best-effort — never block submission on email failure)
    try {
      const { enqueueTransactionalEmail } = await import(
        "@/lib/email/enqueue.server"
      );
      await enqueueTransactionalEmail({
        templateName: "partner-interest-received",
        recipientEmail: data.email,
        idempotencyKey: `partner-interest-${inserted?.id ?? Date.now()}`,
        templateData: {
          contactName: data.contact_name,
          organisation: data.organisation,
          partnerType: data.partner_type,
          tier: data.partnership_tier ?? "",
          interests: data.interests,
          submittedAt: new Date().toLocaleString("en-ZA", {
            timeZone: "Africa/Johannesburg",
          }),
        },
      });
    } catch (e) {
      console.warn("[partner interest email] enqueue failed", e);
    }

    return { ok: true as const };
  });

// ---------- Admin: Founding partners ----------

export const adminListFoundingPartners = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        search: z.string().trim().max(120).optional(),
        status: z.enum(PARTNER_STATUSES).optional(),
        tier: z.enum(PARTNERSHIP_TIERS).optional(),
        partner_type: z.string().trim().max(40).optional(),
      })
      .parse(i ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("founding_partner_interest")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.status) q = q.eq("status", data.status);
    if (data.tier) q = q.eq("partnership_tier", data.tier);
    if (data.partner_type) q = q.eq("partner_type", data.partner_type);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const s = data.search?.toLowerCase();
    const filtered = s
      ? (rows ?? []).filter((r: any) =>
          [r.organisation, r.contact_name, r.email, r.country, r.city]
            .filter(Boolean)
            .some((v: string) => v.toLowerCase().includes(s)),
        )
      : rows ?? [];
    return { rows: filtered };
  });

export const adminUpdateFoundingPartner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(PARTNER_STATUSES).optional(),
        admin_notes: z.string().max(4000).optional().nullable(),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: any = {};
    if (data.status) patch.status = data.status;
    if (data.admin_notes !== undefined) patch.admin_notes = data.admin_notes;
    if (Object.keys(patch).length === 0) return { ok: true as const };
    const { error } = await supabaseAdmin
      .from("founding_partner_interest")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

// ---------- Admin: Prospective learners ----------

export const adminListProspectiveLearners = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        search: z.string().trim().max(120).optional(),
        status: z.enum(LEARNER_STATUSES).optional(),
        program: z.string().trim().max(60).optional(),
        source: z.string().trim().max(80).optional(),
      })
      .parse(i ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("prospective_learners")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (data.status) q = q.eq("status", data.status);
    if (data.source) q = q.eq("source", data.source);
    if (data.program) q = q.contains("programs", [data.program]);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const s = data.search?.toLowerCase();
    const filtered = s
      ? (rows ?? []).filter((r: any) =>
          [r.first_name, r.surname, r.email, r.cell_number, r.source]
            .filter(Boolean)
            .some((v: string) => String(v).toLowerCase().includes(s)),
        )
      : rows ?? [];
    // Aggregate facets for filter UI
    const programs = Array.from(
      new Set((rows ?? []).flatMap((r: any) => r.programs ?? [])),
    ).sort();
    const sources = Array.from(
      new Set((rows ?? []).map((r: any) => r.source).filter(Boolean)),
    ).sort();
    return { rows: filtered, programs, sources };
  });

export const adminUpdateProspectiveLearner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(LEARNER_STATUSES).optional(),
        notes: z.string().max(4000).optional().nullable(),
        partner_email: z.string().trim().email().max(200).optional().nullable(),
        partner_organisation: z.string().trim().max(200).optional().nullable(),
        notify_partner: z.boolean().optional().default(true),
      })
      .parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Load existing row so we can (a) detect a status change and
    // (b) know who to notify even if only notes changed.
    const { data: before, error: readErr } = await supabaseAdmin
      .from("prospective_learners")
      .select("*")
      .eq("id", data.id)
      .single();
    if (readErr || !before) throw new Error(readErr?.message ?? "Not found");

    const patch: any = {};
    if (data.status) patch.status = data.status;
    if (data.notes !== undefined) patch.notes = data.notes;
    if (data.partner_email !== undefined) patch.partner_email = data.partner_email;
    if (data.partner_organisation !== undefined)
      patch.partner_organisation = data.partner_organisation;
    if (Object.keys(patch).length === 0) return { ok: true as const };

    const { error } = await supabaseAdmin
      .from("prospective_learners")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Notify partner if status actually changed and we have a partner email.
    const partnerEmail =
      (patch.partner_email as string | null | undefined) ??
      (before as any).partner_email ??
      null;
    const statusChanged =
      typeof patch.status === "string" && patch.status !== (before as any).status;
    if (statusChanged && partnerEmail && data.notify_partner !== false) {
      try {
        const { enqueueTransactionalEmail } = await import(
          "@/lib/email/enqueue.server"
        );
        const learnerName = [before.first_name, before.surname]
          .filter(Boolean)
          .join(" ")
          .trim() || (before as any).email;
        await enqueueTransactionalEmail({
          templateName: "partner-learner-status-update",
          recipientEmail: partnerEmail,
          idempotencyKey: `learner-status-${data.id}-${patch.status}-${Date.now()}`,
          templateData: {
            partnerContact: partnerEmail.split("@")[0],
            partnerOrganisation:
              patch.partner_organisation ??
              (before as any).partner_organisation ??
              "",
            learnerName,
            previousStatus: (before as any).status ?? "",
            newStatus: patch.status,
            adminNotes:
              (patch.notes as string | null | undefined) ??
              (before as any).notes ??
              "",
            updatedAt: new Date().toLocaleString("en-ZA", {
              timeZone: "Africa/Johannesburg",
            }),
            actorEmail: (context as any).claims?.email ?? "",
          },
        });
      } catch (e) {
        console.warn("[learner status notify] enqueue failed", e);
      }
    }

    return { ok: true as const, notified: statusChanged && !!partnerEmail };
  });

// Audit log for a single learner (admin only).
export const adminGetLearnerAuditLog = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ learner_id: z.string().uuid() }).parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("prospective_learner_audit_log")
      .select("*")
      .eq("learner_id", data.learner_id)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

// Signed download URL for an admin to inspect a partner-uploaded document.
export const adminGetPartnerAttachmentUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ path: z.string().min(3).max(400) }).parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await (supabaseAdmin as any).storage
      .from(ATTACH_BUCKET)
      .createSignedUrl(data.path, 60 * 10);
    if (error || !signed) throw new Error(error?.message ?? "Could not sign URL");
    return { url: signed.signedUrl as string };
  });

export const PARTNER_STATUS_OPTIONS = PARTNER_STATUSES;
export const LEARNER_STATUS_OPTIONS = LEARNER_STATUSES;
export const PARTNERSHIP_TIER_OPTIONS = PARTNERSHIP_TIERS;

// ---------- Admin: invite a prospective learner (create real user account) ----------

const ADMIN_NOTIFY_EMAIL = "nkadimengdanny7@gmail.com";

function generateTempPassword(): string {
  // 14 chars, mixed case + digits + symbol — meets any typical password policy.
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const sym = "!@#$%^&*";
  const all = upper + lower + digits + sym;
  const bytes = new Uint8Array(14);
  crypto.getRandomValues(bytes);
  let out = "";
  out += upper[bytes[0] % upper.length];
  out += lower[bytes[1] % lower.length];
  out += digits[bytes[2] % digits.length];
  out += sym[bytes[3] % sym.length];
  for (let i = 4; i < bytes.length; i++) out += all[bytes[i] % all.length];
  return out;
}

export const adminInviteProspectiveLearner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      id: z.string().uuid(),
      mark_enrolled: z.boolean().optional().default(false),
      // Internship course the admin is placing this learner into. Optional
      // for backward compatibility with the earlier one-click invite path.
      course_slug: z.string().trim().max(80).optional().nullable(),
      course_title: z.string().trim().max(200).optional().nullable(),
      course_url: z.string().trim().url().max(400).optional().nullable(),
      course_note: z.string().trim().max(1000).optional().nullable(),
    }).parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error: readErr } = await supabaseAdmin
      .from("prospective_learners")
      .select("*")
      .eq("id", data.id)
      .single();
    if (readErr || !row) throw new Error(readErr?.message ?? "Learner not found");

    const email = String((row as any).email || "").trim().toLowerCase();
    if (!email) throw new Error("Learner has no email on file");

    const firstName = (row as any).first_name ?? "";
    const surname = (row as any).surname ?? "";
    const fullName = [firstName, surname].filter(Boolean).join(" ").trim() || email;

    // If an auth user already exists for this email, reuse it.
    let userId: string | null = (row as any).onboarded_user_id ?? null;
    let tempPassword: string | null = null;
    let created = false;

    if (!userId) {
      const existing = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
      const match = existing.data?.users?.find(
        (u) => (u.email ?? "").toLowerCase() === email,
      );
      if (match) {
        userId = match.id;
      } else {
        tempPassword = generateTempPassword();
        const { data: newUser, error: createErr } = await supabaseAdmin.auth.admin.createUser({
          email,
          password: tempPassword,
          email_confirm: true,
          user_metadata: {
            full_name: fullName,
            first_name: firstName,
            surname,
            source: "prospective_learner_invite",
            internship_course: data.course_slug ?? null,
          },
        });
        if (createErr || !newUser?.user) {
          throw new Error(createErr?.message ?? "Could not create user account");
        }
        userId = newUser.user.id;
        created = true;
      }
    }

    // Link learner → user, bump status to invited/enrolled, and merge the
    // chosen course into the learner's programs array so it's visible in the
    // admin table and any downstream reporting.
    const newStatus = data.mark_enrolled ? "enrolled" : "invited";
    const existingPrograms: string[] = Array.isArray((row as any).programs)
      ? (row as any).programs
      : [];
    const mergedPrograms = data.course_title
      ? Array.from(new Set([...existingPrograms, data.course_title]))
      : existingPrograms;

    await supabaseAdmin
      .from("prospective_learners")
      .update({
        onboarded_user_id: userId,
        status: newStatus,
        programs: mergedPrograms,
      } as any)
      .eq("id", data.id);

    // Wrap the course link through our click-tracking redirect so we can see
    // which learners actually opened their placement page.
    const trackedCourseUrl = data.course_url
      ? `https://nkadimengdannylcs.com/api/public/invite-click?l=${encodeURIComponent(
          data.id,
        )}&e=${encodeURIComponent(email)}&k=${
          /\/lexpractiq(\b|\/)/.test(data.course_url) ? "lexpractiq" : "course"
        }&s=${encodeURIComponent(data.course_slug ?? "")}&to=${encodeURIComponent(
          data.course_url,
        )}`
      : undefined;

    // Best-effort welcome email (includes temp password only when we just
    // created the account, plus the internship course if the admin picked one).
    try {
      const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue.server");
      await enqueueTransactionalEmail({
        templateName: "welcome",
        recipientEmail: email,
        idempotencyKey: `learner-invite-${data.id}-${newStatus}-${data.course_slug ?? "nocourse"}`,
        templateData: {
          fullName,
          portalUrl: "https://nkadimengdannylcs.com/portal",
          loginEmail: created ? email : undefined,
          tempPassword: created ? tempPassword : undefined,
          courseTitle: data.course_title ?? undefined,
          courseUrl: trackedCourseUrl,
          courseNote: data.course_note ?? undefined,
        },
      });
    } catch (e) {
      console.warn("[invite learner email]", e);
    }

    return { ok: true as const, userId, created, status: newStatus };
  });

// ---------- Public: learner shows interest from the website ----------

const LearnerInterestSchema = z.object({
  first_name: z.string().trim().min(1).max(80),
  surname: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(200),
  cell_number: z.string().trim().max(40).optional().nullable(),
  id_number: z.string().trim().max(30).optional().nullable(),
  programs: z.array(z.string().trim().max(80)).max(10).default([]),
  source: z.string().trim().max(80).optional().nullable(),
  message: z.string().trim().max(2000).optional().nullable(),
});

export const submitLearnerInterest = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => LearnerInterestSchema.parse(i))
  .handler(async ({ data }) => {
    try {
      const { assertRateLimit } = await import("./_rate-limit.server");
      await assertRateLimit({
        scope: "intake:learner_interest",
        key: clientKey(),
        windowMinutes: 15,
        limit: 5,
      });
    } catch (e) {
      throw e instanceof Error ? e : new Error("Rate limit exceeded");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.trim().toLowerCase();

    // Upsert-style: if the same email already exists in prospective_learners, update it.
    const { data: existing } = await supabaseAdmin
      .from("prospective_learners")
      .select("id, status")
      .eq("email", email)
      .maybeSingle();

    let learnerId: string;
    if (existing) {
      const { error: updErr } = await supabaseAdmin
        .from("prospective_learners")
        .update({
          first_name: data.first_name,
          surname: data.surname,
          cell_number: data.cell_number ?? null,
          id_number: data.id_number ?? null,
          programs: data.programs,
          source: data.source ?? "website_learner_form",
          notes: data.message ?? null,
        } as any)
        .eq("id", (existing as any).id);
      if (updErr) throw new Error(updErr.message);
      learnerId = (existing as any).id;
    } else {
      const { data: inserted, error: insErr } = await supabaseAdmin
        .from("prospective_learners")
        .insert({
          first_name: data.first_name,
          surname: data.surname,
          email,
          cell_number: data.cell_number ?? null,
          id_number: data.id_number ?? null,
          programs: data.programs,
          source: data.source ?? "website_learner_form",
          notes: data.message ?? null,
          status: "new",
        } as any)
        .select("id")
        .single();
      if (insErr || !inserted) throw new Error(insErr?.message ?? "Could not save");
      learnerId = (inserted as any).id;
    }

    // Confirmation email to learner (best-effort)
    try {
      const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue.server");
      await enqueueTransactionalEmail({
        templateName: "enquiry-received",
        recipientEmail: email,
        idempotencyKey: `learner-interest-${learnerId}`,
        templateData: {
          fullName: [data.first_name, data.surname].filter(Boolean).join(" "),
          subject: "Thanks for your interest in our programs",
          message:
            "We received your details and someone from our team will be in touch shortly with next steps for the programs you selected.",
        },
      });
    } catch (e) {
      console.warn("[learner interest confirm email]", e);
    }

    // Notify admin (best-effort)
    try {
      const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue.server");
      await enqueueTransactionalEmail({
        templateName: "security-alert",
        recipientEmail: ADMIN_NOTIFY_EMAIL,
        idempotencyKey: `learner-interest-admin-${learnerId}`,
        subjectOverride: `New learner interest: ${data.first_name} ${data.surname}`,
        templateData: {
          eventType: "learner_interest",
          severity: "info",
          email,
          summary: `${data.first_name} ${data.surname} showed interest in ${data.programs.join(", ") || "our programs"}.`,
          details: [
            `Cell: ${data.cell_number ?? "—"}`,
            `ID: ${data.id_number ?? "—"}`,
            `Source: ${data.source ?? "website_learner_form"}`,
            `Message: ${data.message ?? "—"}`,
          ].join("\n"),
          occurredAt: new Date().toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" }),
          reviewUrl: "https://nkadimengdannylcs.com/portal/admin/prospective-learners",
        },
      });
    } catch (e) {
      console.warn("[learner interest admin notify]", e);
    }

    return { ok: true as const, id: learnerId };
  });



// ---------- Admin: recent invite/course link clicks ----------
export const adminListInviteClicks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("invite_link_clicks")
      .select("id, learner_id, learner_email, destination, destination_kind, course_slug, clicked_at")
      .order("clicked_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { clicks: (data ?? []) as any[] };
  });
